import { ApiError, api, type Vendor } from '../../lib/api'
import { exitApi, type ExitSummary, type ExitView } from '../../lib/exit'
import { stagePosition } from './words'

// Every exit, for the board: GET /v1/exits, or the reads it replaced.
//
// # WHY THERE IS A SECOND ROAD
//
// The summary endpoint arrives with this screen, and a console can meet an API
// that does not have it yet (a local stack built before it, a rollout where
// the console goes first). A 404 there is not a broken screen, so the board
// assembles the same shape from the three reads the old screen used and the
// per-vendor exit read. It is slower and has no plan dates or blockers, so it
// shows none: nothing here invents what the API did not say.

export async function loadExitBoard(): Promise<ExitSummary[]> {
  try {
    const r = await exitApi.summary()
    return r.exits ?? []
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) throw err
  }
  return assemble()
}

// One vendor's exit summary for the workspace, or null where the API has no
// summary read or this person may not read the register.
export async function loadVendorSummary(vendorId: string): Promise<ExitSummary | null> {
  try {
    const r = await exitApi.summary(vendorId)
    return (r.exits ?? []).find((x) => x.vendorId === vendorId) ?? null
  } catch {
    return null
  }
}

const YEAR_MS = 365 * 86400000

// Whole days from a recorded start to a recorded end, or to now.
function daysSince(from: string | undefined, to: string | undefined): number | undefined {
  if (!from) return undefined
  const start = new Date(from).getTime()
  const end = to ? new Date(to).getTime() : Date.now()
  if (Number.isNaN(start) || Number.isNaN(end)) return undefined
  return Math.max(0, Math.floor((end - start) / 86400000))
}

async function assemble(): Promise<ExitSummary[]> {
  const [proposals, open, done] = await Promise.all([
    exitApi.proposals(),
    api.vendors({ status: ['OFFBOARDING'] }),
    api.vendors({ status: ['OFFBOARDED'] }),
  ])
  const seeds: { vendor: Pick<Vendor, 'id' | 'legalName' | 'displayName' | 'vendorCode' | 'status'> & { inherentTier?: number } }[] = [
    ...(proposals.proposals ?? []).map((p) => ({
      vendor: { id: p.vendorId, legalName: p.legalName, displayName: p.legalName, vendorCode: p.vendorCode, status: p.vendorStatus },
    })),
    ...(open.vendors ?? []).map((v) => ({ vendor: v })),
    ...(done.vendors ?? []).map((v) => ({ vendor: v })),
  ]
  const views = await Promise.all(seeds.map((s) => exitApi.vendorExit(s.vendor.id).catch(() => null)))
  const out: ExitSummary[] = []
  seeds.forEach((s, i) => {
    const view = views[i]
    if (view) {
      const row = fromView(s.vendor, view)
      if (row) out.push(row)
    }
  })
  return out
}

function fromView(
  v: Pick<Vendor, 'id' | 'legalName' | 'displayName' | 'vendorCode' | 'status'> & { inherentTier?: number },
  view: ExitView,
): ExitSummary | null {
  const chain = view.exitApproval
  const exit = chain.exit
  const lane: ExitSummary['laneState'] =
    view.status === 'OFFBOARDING' ? 'UNDER_WAY' : view.status === 'OFFBOARDED' ? 'SIGNED_OUT' : 'PROPOSED'
  const closedRecord = lane !== 'SIGNED_OUT' || exit?.status === 'CLOSED'
  if (lane === 'SIGNED_OUT' && exit?.closedAt && Date.now() - new Date(exit.closedAt).getTime() > YEAR_MS) return null

  const stages = closedRecord
    ? chain.stages.map((s) => ({ key: s.key, job: s.job, state: s.state, signedBy: s.signedBy, signedAt: s.signedAt, items: [] }))
    : []
  const index = closedRecord ? stagePosition(chain.stages) : 0
  const current = lane !== 'SIGNED_OUT' ? chain.stages[index] : undefined
  const declined = lane === 'UNDER_WAY' && current?.state === 'declined'
  return {
    vendorId: v.id,
    vendorName: v.displayName?.trim() || v.legalName,
    legalName: v.legalName,
    vendorCode: v.vendorCode,
    vendorStatus: view.status,
    tier: v.inherentTier ?? null,
    laneState: lane,
    exitId: exit?.id,
    recorded: exit?.recorded ?? false,
    triggerKind: exit?.triggerKind ?? '',
    reason: exit?.reason,
    proposedBy: exit?.proposedBy,
    proposedAt: exit?.proposedAt,
    openedBy: exit?.openedBy,
    openedAt: exit?.openedAt,
    closedBy: exit?.closedBy,
    closedAt: exit?.closedAt,
    stageCount: stages.length,
    stageIndex: index,
    stages,
    currentJob: current?.job,
    currentRoles: current?.roleNames ?? [],
    blocker: declined ? 'Declined by ' + (current?.signedBy ?? '') + (current?.note ? ': ' + current.note : '') : undefined,
    daysOpen: daysSince(lane === 'PROPOSED' ? exit?.proposedAt : exit?.openedAt, lane === 'SIGNED_OUT' ? exit?.closedAt : undefined),
  }
}
